import { Route } from '@/core/router.types'
import type LegacyCoursesRedirect from './LegacyCoursesRedirect'
import type LegacyStartCourseRedirect from './LegacyStartCourseRedirect'
import type MyCourses from './MyCourses'
import type StartCourse from './StartCourse'

const LEARNER_ROLES = ['customer_success', 'customer_manager', 'global_customer_manager', 'participant']

// App.tsx writes `title` into document.title, so these are the browser-tab text. A retired address
// and the address that replaced it are the same screen, so they share one definition rather than
// two copies that can drift apart.
//
// Still English, like every other route title in the app: 26 of the 27 across the route tables are
// display strings, not Locize keys. FS-386 moves them all over. Localizing only these two would
// leave the retired address and the current one disagreeing in the tab for the same screen.
const MY_COURSES_TITLE = 'My Courses'
const START_COURSE_TITLE = 'Start course'

/**
 * My courses pages: the list, and the start / continue screen a course opens through.
 *
 * Both also answer on /my/trainings, where they lived before the rename. Mail already sent
 * addresses a course by id on that path, and neither delivered mail nor a learner's bookmarks can
 * be rewritten, so the old addresses keep resolving - by forwarding to the current ones, so a
 * bookmark taken from an old mail becomes a current one on first use.
 */
export default [
  {
    menu: { label: 'my_courses_nav', icon: 'FavoriteTraining' },
    path: '/my/courses',
    query: '',
    authorize: LEARNER_ROLES,
    flushTop: true,
    component: () => import(/* webpackChunkName: "MyCourses" */ /* webpackMode: "eager" */ './MyCourses'),
    response: (data: unknown) => ({
      title: MY_COURSES_TITLE,
      description: '',
      props: data,
    }),
  } as unknown as Route<typeof MyCourses>,
  {
    path: '/my/courses/:courseCode',
    query: '',
    authorize: LEARNER_ROLES,
    // Learners are sent straight here by email, so the screen stands on its own: no sidebar, no
    // app bar, nothing to wander off into before starting the course.
    standalone: true,
    component: () => import(/* webpackChunkName: "StartCourse" */ /* webpackMode: "eager" */ './StartCourse'),
    response: (data: unknown) => ({
      title: START_COURSE_TITLE,
      description: '',
      props: data,
    }),
  } as unknown as Route<typeof StartCourse>,
  {
    path: '/my/trainings',
    query: '',
    authorize: LEARNER_ROLES,
    component: () =>
      import(/* webpackChunkName: "LegacyCoursesRedirect" */ /* webpackMode: "eager" */ './LegacyCoursesRedirect'),
    response: (data: unknown) => ({
      title: MY_COURSES_TITLE,
      description: '',
      props: data,
    }),
  } as unknown as Route<typeof LegacyCoursesRedirect>,
  {
    // Delivered invitation mail addresses the course by id on the old path. Forwarded rather than
    // rendered in place, so both retired addresses behave the same way and the learner ends up on
    // the canonical URL.
    path: '/my/trainings/:courseId',
    query: '',
    authorize: LEARNER_ROLES,
    standalone: true,
    component: () =>
      import(
        /* webpackChunkName: "LegacyStartCourseRedirect" */ /* webpackMode: "eager" */ './LegacyStartCourseRedirect'
      ),
    response: (data: unknown) => ({
      title: START_COURSE_TITLE,
      description: '',
      props: data,
    }),
  } as unknown as Route<typeof LegacyStartCourseRedirect>,
] as const
